import React, { useState } from 'react';

// --- Shared SOWASH / NOVA UI Components ---
const inpStyle = { 
  padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: '7px', 
  fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box', 
  background: '#fff', color: '#111827' 
};

const Section = ({ title, badge, color = '#1e3a8a', children }) => (
  <div style={{ marginBottom: '24px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderLeft: `4px solid ${color}`, borderRadius: '6px', padding: '10px 14px', marginBottom: '16px' }}>
      <div>
        <span style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
      </div>
      {badge && <span style={{ background: color, color: 'white', padding: '2px 10px', borderRadius: '50px', fontSize: '12px', fontWeight: '700' }}>{badge}</span>}
    </div>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
      <span style={{ fontWeight: '600', color: '#374151' }}>{label}</span>
    </div>
    {children}
  </div>
);

const SelectionCard = ({ type = 'checkbox', checked, onChange, value, label, name, accent = '#1e3a8a', darkTheme = false }) => {
  const bg = darkTheme 
    ? (checked ? `${accent}40` : 'rgba(255,255,255,0.05)') 
    : (checked ? `${accent}18` : '#fafafa');
  const border = darkTheme
    ? (checked ? accent : 'rgba(255,255,255,0.2)')
    : (checked ? accent : '#e5e7eb');
  const textCol = darkTheme ? '#ffffff' : '#111827';

  return (
    <label style={{ display: 'flex', gap: '8px', padding: '10px 12px', border: `1.5px solid ${border}`, borderRadius: '8px', cursor: 'pointer', background: bg, alignItems: 'center', transition: 'all 0.2s ease' }}>
      <input type={type} name={name} value={value} checked={checked} onChange={onChange} style={{ accentColor: accent, width: '15px', height: '15px', marginTop: '1px' }} />
      <div style={{ fontSize: '13px', fontWeight: '600', color: textCol, flex: 1, lineHeight: '1.3' }}>{label}</div>
    </label>
  );
};

const FileUploadCard = ({ label, accent = '#2563eb', name, fileData, handleFileChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '220px' }}>
    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>{label}</label>
    <label style={{ border: `1.5px dashed ${fileData ? '#059669' : accent}66`, padding: '16px', borderRadius: '8px', textAlign: 'center', background: fileData ? '#ecfdf5' : `${accent}0a`, cursor: 'pointer', transition: 'all 0.2s' }}>
      <input type="file" name={name} onChange={handleFileChange} style={{ display: 'none' }} />
      <span style={{ fontSize: '13px', color: fileData ? '#059669' : accent, fontWeight: '700', wordBreak: 'break-all' }}>
        {fileData ? `✅ ${fileData.name}` : 'Click to Upload File'}
      </span>
    </label>
  </div>
);

const OptionGrid = ({ options, type = 'radio', name, state, handleChange, handleCheckChange, accent, darkTheme = false }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '10px' }}>
    {options.map(opt => {
      const isChecked = type === 'radio' ? state[name] === opt : (state[name] || []).includes(opt);
      return (
        <SelectionCard key={opt} type={type} label={opt} value={opt} name={name} accent={accent} checked={isChecked} onChange={type === 'radio' ? handleChange : (e) => handleCheckChange(e, name)} darkTheme={darkTheme} />
      );
    })}
  </div>
);

// --- Main Form Component ---
const NovaOnboardingForm = () => {
  const [formData, setFormData] = useState({
    fullName: '', cnic: '', dob: '', nationality: '', mobile: '', email: '',
    permanentAddress: '', currentAddress: '', emergencyName: '', emergencyContact: '', relation: '', bloodGroup: '',
    jobSource: [], jobSourceOther: '', ref1Name: '', ref1Contact: '', ref2Name: '', ref2Contact: '',
    positionApplied: '', interviewDate: '', interviewedBy: '', hiringDecision: '', mandatoryDocs: [], backgroundVerification: '', rightToWork: '',
    empId: '', doj: '', employmentType: '', department: [], designation: '', designationOther: '', reportingManager: '', salaryStructure: '', probationEnd: '',
    accessLevel: [], novaAccount: '', mobileDeviceAssigned: '', deviceId: '', gpsTracking: '', profileStatus: '', activationDate: '',
    uniform: [], ppe: [], equipment: [], assetSerials: '', digitalSignature: '', assetIssuedBy: '', hrApproval: '', opsApproval: '', finalStatus: '',
    photoFile: null, cnicFrontFile: null, cnicBackFile: null // New State for Files
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycby5zTZySZrCxJCGFDmEBby2zT_pE80Z5jbUtdSQUcWTSAjVtzeqDXGjSKrb3B1EzRHY/exec';

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  
  const handleCheckChange = (e, name) => {
    const value = e.target.value;
    setFormData(prev => {
      const current = prev[name] || [];
      return e.target.checked ? { ...prev, [name]: [...current, value] } : { ...prev, [name]: current.filter(item => item !== value) };
    });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    }
  };

 const handleSave = async () => {
    setIsSubmitting(true);
    
    const payload = { ...formData };
    
    // Convert arrays to strings for Google Sheets
    Object.keys(payload).forEach(key => {
      if (Array.isArray(payload[key])) {
        payload[key] = payload[key].join(', ');
      }
    });

    // Send only file names to Google Sheets as proof of upload
    payload.photoFile = formData.photoFile ? `Uploaded: ${formData.photoFile.name}` : 'Not Uploaded';
    payload.cnicFrontFile = formData.cnicFrontFile ? `Uploaded: ${formData.cnicFrontFile.name}` : 'Not Uploaded';
    payload.cnicBackFile = formData.cnicBackFile ? `Uploaded: ${formData.cnicBackFile.name}` : 'Not Uploaded';

    try {
      const response = await fetch(SCRIPT_URL, {
        method: 'POST',
        headers: {
          // Yeh header CORS error se bachata hai aur data ko successfully GAS tak pohnchata hai
          "Content-Type": "text/plain;charset=utf-8",
        },
        body: JSON.stringify(payload),
      });
      
      const result = await response.json();
      
      // Console mein check karne ke liye ke waqai kya response aya hai
      console.log("Google Apps Script Response: ", result); 
      
      if(result.success) {
        alert("✅ Profile Saved to Google Sheets!");
      } else {
        alert("❌ Error from Sheet: " + result.message);
      }
    } catch (error) {
      console.error("Fetch Error: ", error);
      alert("❌ Failed to push data. Please check console logs.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div style={{ height: '100vh', maxHeight: '100vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif", backgroundColor: '#f1f5f9', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

        {/* HEADER */}
        <div style={{ background: 'white', borderRadius: '14px 14px 0 0', padding: '18px 24px', display: 'flex', alignItems: 'center', gap: '16px', borderBottom: '4px solid #1e3a8a' }}>
          <div style={{ width: '54px', height: '54px', borderRadius: '10px', background: 'linear-gradient(135deg, #1e3a8a, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', flexShrink: 0, color: 'white', fontWeight: '900' }}>N</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>SOWASH – NOVA EMPLOYEE ONBOARDING</div>
            <div style={{ fontSize: '13px', color: '#dc2626', fontWeight: '600', marginTop: '2px' }}>(Mandatory Prior to System Activation & Asset Issuance)</div>
          </div>
        </div>

        {/* BODY */}
        <div style={{ background: '#fff', padding: '24px', borderRadius: '0 0 14px 14px', boxShadow: '0 20px 50px rgba(0,0,0,0.1)', marginBottom: '40px' }}>

          {/* SECTION 1 */}
          <Section title="1. Personal Information" color="#1e3a8a">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              <Field label="Full Legal Name (As per CNIC/Passport)"><input style={inpStyle} name="fullName" value={formData.fullName} onChange={handleChange} /></Field>
              <Field label="CNIC / ID Number"><input style={inpStyle} name="cnic" value={formData.cnic} onChange={handleChange} /></Field>
              <Field label="Date of Birth"><input type="date" style={inpStyle} name="dob" value={formData.dob} onChange={handleChange} /></Field>
              <Field label="Nationality"><input style={inpStyle} name="nationality" value={formData.nationality} onChange={handleChange} /></Field>
              <Field label="Mobile Number"><input style={inpStyle} name="mobile" value={formData.mobile} onChange={handleChange} /></Field>
              <Field label="Email Address"><input type="email" style={inpStyle} name="email" value={formData.email} onChange={handleChange} /></Field>
              <div style={{ gridColumn: '1 / -1' }}><Field label="Permanent Address"><input style={inpStyle} name="permanentAddress" value={formData.permanentAddress} onChange={handleChange} /></Field></div>
              <div style={{ gridColumn: '1 / -1' }}><Field label="Current Residential Address"><input style={inpStyle} name="currentAddress" value={formData.currentAddress} onChange={handleChange} /></Field></div>
              <Field label="Emergency Contact Name"><input style={inpStyle} name="emergencyName" value={formData.emergencyName} onChange={handleChange} /></Field>
              <Field label="Emergency Contact Number"><input style={inpStyle} name="emergencyContact" value={formData.emergencyContact} onChange={handleChange} /></Field>
              <Field label="Relationship to Emergency Contact"><input style={inpStyle} name="relation" value={formData.relation} onChange={handleChange} /></Field>
              <Field label="Blood Group (Optional)"><input style={inpStyle} name="bloodGroup" value={formData.bloodGroup} onChange={handleChange} /></Field>
            </div>
          </Section>

          {/* SECTION 2 */}
          <Section title="2. Photo & ID Upload" badge="MANDATORY" color="#2563eb">
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <FileUploadCard label="Passport Size Photograph" name="photoFile" fileData={formData.photoFile} handleFileChange={handleFileChange} accent="#2563eb" />
              <FileUploadCard label="CNIC Copy – Front" name="cnicFrontFile" fileData={formData.cnicFrontFile} handleFileChange={handleFileChange} accent="#2563eb" />
              <FileUploadCard label="CNIC Copy – Back" name="cnicBackFile" fileData={formData.cnicBackFile} handleFileChange={handleFileChange} accent="#2563eb" />
            </div>
          </Section>

          {/* SECTION 3 */}
          <Section title="3. Job Source & References" color="#7c3aed">
            <Field label="How Did You Learn About This Position?">
              <OptionGrid options={['Company Website', 'Social Media', 'Employee Referral', 'Walk-In', 'Job Portal']} type="checkbox" name="jobSource" state={formData} handleCheckChange={handleCheckChange} accent="#7c3aed" />
            </Field>
            <div style={{ marginTop: '10px', marginBottom: '16px' }}>
              <Field label="Other Source"><input style={inpStyle} name="jobSourceOther" value={formData.jobSourceOther} onChange={handleChange} /></Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              <Field label="Reference 1 – Name"><input style={inpStyle} name="ref1Name" value={formData.ref1Name} onChange={handleChange} /></Field>
              <Field label="Reference 1 – Contact Number"><input style={inpStyle} name="ref1Contact" value={formData.ref1Contact} onChange={handleChange} /></Field>
              <Field label="Reference 2 – Name (Optional)"><input style={inpStyle} name="ref2Name" value={formData.ref2Name} onChange={handleChange} /></Field>
              <Field label="Reference 2 – Contact Number (Optional)"><input style={inpStyle} name="ref2Contact" value={formData.ref2Contact} onChange={handleChange} /></Field>
            </div>
          </Section>

          {/* SECTION 4 */}
          <Section title="4. Hiring & Documentation Record" color="#0891b2">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
              <Field label="Position Applied For"><input style={inpStyle} name="positionApplied" value={formData.positionApplied} onChange={handleChange} /></Field>
              <Field label="Interview Date"><input type="date" style={inpStyle} name="interviewDate" value={formData.interviewDate} onChange={handleChange} /></Field>
              <Field label="Interviewed By"><input style={inpStyle} name="interviewedBy" value={formData.interviewedBy} onChange={handleChange} /></Field>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <Field label="Hiring Decision">
                <OptionGrid options={['Selected', 'Rejected', 'Waitlisted']} type="radio" name="hiringDecision" state={formData} handleChange={handleChange} accent="#0891b2" />
              </Field>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <Field label="Mandatory Hiring Documentation (Pre-Activation)">
                <OptionGrid options={['Hiring Questionnaire - Part 1 Completed', 'Hiring Questionnaire - Part 2 Completed', 'Offer Letter Issued', 'Employment Contract Signed', 'CV Submitted', 'Educational Documents Attached (If Required)', 'Medical Fitness Certificate (If Applicable)', 'Police Character Certificate (If Applicable)']} type="checkbox" name="mandatoryDocs" state={formData} handleCheckChange={handleCheckChange} accent="#0891b2" />
              </Field>
            </div>

            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <Field label="Background Verification Status">
                  <OptionGrid options={['Completed', 'Pending', 'Not Required']} type="radio" name="backgroundVerification" state={formData} handleChange={handleChange} accent="#0891b2" />
                </Field>
              </div>
              <div style={{ flex: 1, minWidth: '150px' }}>
                <Field label="Right to Work Verified">
                  <OptionGrid options={['Yes', 'No']} type="radio" name="rightToWork" state={formData} handleChange={handleChange} accent="#0891b2" />
                </Field>
              </div>
            </div>
          </Section>

          {/* SECTION 5 */}
          <Section title="5. Employment Details" color="#059669">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
              <Field label="Employee ID"><input style={{...inpStyle, background: '#f8fafc', color: '#9ca3af'}} name="empId" value={formData.empId} onChange={handleChange} placeholder="(System Generated by NOVA)" disabled /></Field>
              <Field label="Date of Joining"><input type="date" style={inpStyle} name="doj" value={formData.doj} onChange={handleChange} /></Field>
              <Field label="Reporting Manager"><input style={inpStyle} name="reportingManager" value={formData.reportingManager} onChange={handleChange} /></Field>
              <Field label="Probation End Date (If Applicable)"><input type="date" style={inpStyle} name="probationEnd" value={formData.probationEnd} onChange={handleChange} /></Field>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '16px' }}>
              <Field label="Employment Type">
                <OptionGrid options={['Permanent', 'Contract', 'Temporary', 'Probation']} type="radio" name="employmentType" state={formData} handleChange={handleChange} accent="#059669" />
              </Field>
              <Field label="Department">
                <OptionGrid options={['Operations', 'Projects', 'Technology (NOVA)', 'Finance', 'Marketing', 'Compliance / HSE', 'HR', 'Administration']} type="checkbox" name="department" state={formData} handleCheckChange={handleCheckChange} accent="#059669" />
              </Field>
              <Field label="Designation">
                <OptionGrid options={['Field Officer - Level 1', 'Field Officer - Level 2', 'Field Officer - Level 3', 'Technician - Level 1', 'Technician - Level 2', 'Senior Technician', 'Site Supervisor', 'Senior Supervisor', 'Operations Supervisor', 'Operations Lead', 'Regional Operations Manager', 'Deployment Manager', 'Manager', 'Other']} type="radio" name="designation" state={formData} handleChange={handleChange} accent="#059669" />
              </Field>
              {formData.designation === 'Other' && (
                <Field label="Specify Other Designation"><input style={inpStyle} name="designationOther" value={formData.designationOther} onChange={handleChange} /></Field>
              )}
              <Field label="Salary Structure">
                <OptionGrid options={['Monthly', 'Daily', 'Per Job', 'Project Based']} type="radio" name="salaryStructure" state={formData} handleChange={handleChange} accent="#059669" />
              </Field>
            </div>
          </Section>

          {/* SECTION 6 */}
          <Section title="6. NOVA System Access Configuration" color="#ea580c">
            <div style={{ marginBottom: '16px' }}>
              <Field label="Access Level Assigned">
                <OptionGrid options={['Field Operator (Mobile App Access)', 'Technician Access', 'Supervisor Access', 'Operations Management Access', 'Admin Access', 'Read-Only Access']} type="checkbox" name="accessLevel" state={formData} handleCheckChange={handleCheckChange} accent="#ea580c" />
              </Field>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <Field label="NOVA Account Created">
                <OptionGrid options={['Yes', 'No']} type="radio" name="novaAccount" state={formData} handleChange={handleChange} accent="#ea580c" />
              </Field>
              <Field label="GPS Tracking Enabled">
                <OptionGrid options={['Yes', 'No']} type="radio" name="gpsTracking" state={formData} handleChange={handleChange} accent="#ea580c" />
              </Field>
              <Field label="Mobile Device Assigned">
                <OptionGrid options={['Yes', 'No']} type="radio" name="mobileDeviceAssigned" state={formData} handleChange={handleChange} accent="#ea580c" />
                {formData.mobileDeviceAssigned === 'Yes' && (
                  <input style={{...inpStyle, marginTop: '8px'}} name="deviceId" value={formData.deviceId} onChange={handleChange} placeholder="Device ID" />
                )}
              </Field>
            </div>

            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
               <div style={{ flex: 2, minWidth: '300px' }}>
                 <Field label="Profile Status">
                   <OptionGrid options={['Draft', 'Under HR Review', 'Under Operations Review', 'Approved', 'Activated', 'Suspended', 'Terminated']} type="radio" name="profileStatus" state={formData} handleChange={handleChange} accent="#ea580c" />
                 </Field>
               </div>
               <div style={{ flex: 1, minWidth: '150px' }}>
                 <Field label="Activation Date"><input style={{...inpStyle, background: '#f8fafc', color: '#9ca3af'}} name="activationDate" value={formData.activationDate} onChange={handleChange} placeholder="(System Generated)" disabled /></Field>
               </div>
            </div>
          </Section>

          {/* SECTION 7 */}
          <Section title="7. Uniform & Company Asset Issuance" color="#dc2626">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <Field label="Uniform Issued">
                <OptionGrid options={['Shirt', 'Trouser', 'Jacket', 'Safety Vest']} type="checkbox" name="uniform" state={formData} handleCheckChange={handleCheckChange} accent="#dc2626" />
              </Field>
              <Field label="PPE Issued">
                <OptionGrid options={['Helmet', 'Gloves', 'Safety Shoes', 'Harness', 'Goggles']} type="checkbox" name="ppe" state={formData} handleCheckChange={handleCheckChange} accent="#dc2626" />
              </Field>
              <Field label="Operational Equipment Issued">
                <OptionGrid options={['Cleaning Kit', 'Tool Kit', 'Access Card', 'Vehicle Access', 'Mobile Phone', 'Laptop']} type="checkbox" name="equipment" state={formData} handleCheckChange={handleCheckChange} accent="#dc2626" />
              </Field>
            </div>
            
            <Field label="Asset Serial Numbers / Details">
              <textarea style={{ ...inpStyle, height: '70px', resize: 'vertical' }} name="assetSerials" value={formData.assetSerials} onChange={handleChange} />
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '16px' }}>
              <Field label="Employee Digital Signature"><input style={inpStyle} name="digitalSignature" value={formData.digitalSignature} onChange={handleChange} /></Field>
              <Field label="Asset Issued By (Name & Date)"><input style={inpStyle} name="assetIssuedBy" value={formData.assetIssuedBy} onChange={handleChange} /></Field>
            </div>
          </Section>

          {/* FINAL APPROVAL WORKFLOW BANNER (Dark Theme Support) */}
          <div style={{ background: 'linear-gradient(135deg, #0f172a, #1e3a8a)', borderRadius: '12px', padding: '20px 24px', color: 'white', marginBottom: '24px', border: '4px solid #f1f5f9', outline: '1px solid #e2e8f0' }}>
            <h2 style={{ fontSize: '15px', fontWeight: '800', margin: '0 0 16px 0', textTransform: 'uppercase', letterSpacing: '1px', color: '#93c5fd' }}>FINAL APPROVAL WORKFLOW</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <Field label={<span style={{ color: 'white' }}>HR Approval</span>}>
                <OptionGrid options={['Approved', 'Rejected']} type="radio" name="hrApproval" state={formData} handleChange={handleChange} accent="#60a5fa" darkTheme={true} />
              </Field>
              <Field label={<span style={{ color: 'white' }}>Operations Approval</span>}>
                <OptionGrid options={['Approved', 'Rejected']} type="radio" name="opsApproval" state={formData} handleChange={handleChange} accent="#60a5fa" darkTheme={true} />
              </Field>
              <Field label={<span style={{ color: '#34d399' }}>Final Status</span>}>
                <OptionGrid options={['Activated', 'On Hold', 'Rejected']} type="radio" name="finalStatus" state={formData} handleChange={handleChange} accent="#34d399" darkTheme={true} />
              </Field>
            </div>
          </div>

          {/* SAVE BUTTON */}
          <button 
            onClick={handleSave} 
            disabled={isSubmitting} 
            style={{ 
              width: '100%', padding: '16px', 
              background: isSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #1e3a8a, #2563eb)', 
              color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', 
              fontWeight: '800', cursor: isSubmitting ? 'not-allowed' : 'pointer', 
              boxShadow: '0 8px 20px rgba(30,58,138,0.3)', letterSpacing: '0.5px' 
            }}
          >
            {isSubmitting ? '⏳ SAVING...' : '💾 SAVE ONBOARDING PROFILE'}
          </button>
          
        </div>
      </div>
    </div>
  );
};

export default NovaOnboardingForm;